import { FC, memo, PropsWithChildren, SyntheticEvent, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { TaskCenterQueries, TaskCenterTypes, useTaskCenterMutations } from '@frontend/api-task-center';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { useAppScopeStore } from '@frontend/scope';
import { useDeleteTask, UseDeleteTaskProps, useTaskCenterStore } from '@frontend/task-center-scope';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Button,
  Chip,
  NakedButton,
  SpinningLoader,
  Text,
  useAlert,
  usePopoverMenu,
} from '@frontend/design-system';
import { contactPanelTaskListTrackingIds, taskCenterTrackingIds } from '../../tracking-ids';
import { convertUTCToLocal, formatDate, getLocationTimeZone } from '../../utils';
import { StatusUpdater } from '../status-updater/status-updater';
import { TaskItemPopoverMenu } from '../task-item/task-item-popover-menu';
import { TaskAIIcon } from './task-ai-icon';
import {
  containerStyle,
  dueDateChipStyle,
  firstTaskItemRow,
  loaderStyles,
  taskTitleContainer,
} from './task-item.styles';
import { TaskPriority } from './task-priority';

interface Props extends TaskCenterTypes.Task {
  trackingIds: typeof contactPanelTaskListTrackingIds | typeof taskCenterTrackingIds;
  isFromContactPanel?: boolean;
}

export const TaskItem: FC<PropsWithChildren<Props>> = memo(
  ({ dueDate, title, status, id, locationId, priority, children, trackingIds, isFromContactPanel, ...rest }) => {
    const {
      locationsData,
      setTaskMode,
      setSelectedTask,
      assignees,
      isTaskCenterOpen,
      setIsTaskCenterOpen,
      setIsFromContactPanel,
      isAIRCreatorEnabled,
    } = useTaskCenterStore([
      'locationsData',
      'setTaskMode',
      'setSelectedTask',
      'assignees',
      'isTaskCenterOpen',
      'setIsTaskCenterOpen',
      'setIsFromContactPanel',
      'isAIRCreatorEnabled',
    ]);
    const { t } = useTranslation('task-center');
    const user = getUser();
    const queryClient = useQueryClient();
    const alert = useAlert();
    const { isSingleTypeScope } = useAppScopeStore();

    const timezone = getLocationTimeZone(locationsData, locationId);

    const date = useMemo(() => {
      if (dueDate) {
        return formatDate(
          `MMM D, YYYY${rest?.dueDateHasTime ? '; h:mm A' : ''}`,
          convertUTCToLocal({ date: dueDate, tz: timezone, hasTime: rest?.dueDateHasTime })
        );
      }
      return t('No Due Date');
    }, [dueDate, rest?.dueDateHasTime, t, timezone]);

    const location: string = locationsData.find((loc) => loc.id === locationId)?.name || 'Location Name';
    const assignee = assignees.find((assign) => assign.userId === rest.assignee);
    const assigneeName = getFullName(assignee ?? {});

    const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu();

    const { taskUpdateMutation } = useTaskCenterMutations();

    const { mutate: updateTask, isLoading: isUpdating } = taskUpdateMutation;

    const deleteTaskTrackingIds = useMemo<UseDeleteTaskProps['trackingIds']>(
      () => ({
        deleteClick: trackingIds.list.deleteTaskFromList,
        deleteConfirmClick: trackingIds.deleteTaskConfirm,
        deleteCancelClick: trackingIds.deleteTaskCancel,
      }),
      [trackingIds]
    );

    const {
      ConfirmationModal,
      modalProps: confirmationModalProps,
      isDeleting,
      triggerProps,
    } = useDeleteTask({ id, trackingIds: deleteTaskTrackingIds });

    const isLoading = isDeleting || isUpdating;

    const onClickEdit = () => {
      setSelectedTask({ dueDate, title, status, id, locationId, priority, ...rest });
      setTaskMode('edit');
      if (isFromContactPanel) {
        setIsFromContactPanel(true);
      }
      if (!isTaskCenterOpen) {
        setIsTaskCenterOpen(true);
      }
      close();
    };

    const updateTaskStatus = (newStatus: TaskCenterTypes.TaskStatus) => {
      if (id && user?.userID) {
        updateTask(
          { id, status: newStatus, updatedBy: user.userID },
          {
            onError: () => {
              close();
              alert.error(t('Failed to update task'));
            },
            onSuccess: () => {
              close();
              alert.success(t('Task updated successfully'));
              queryClient.invalidateQueries(TaskCenterQueries.queryEndpointKeys.tasksList);
            },
          }
        );
      }
    };

    return (
      <NakedButton
        css={[containerStyle, isFromContactPanel && { padding: theme.spacing(2) }]}
        onClick={onClickEdit}
        trackingId={trackingIds.list.openTaskDetails}
        aria-label={t('Open task details')}
      >
        <div className='row' css={firstTaskItemRow}>
          <StatusUpdater
            status={status}
            onAction={(e: SyntheticEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              updateTaskStatus(TaskCenterTypes.TaskStatus.STATUS_COMPLETED);
            }}
            trackingId={trackingIds.list.switchTaskFromTaskList}
          />
          <Text textAlign='left' size='large' weight='bold' css={taskTitleContainer}>
            {title}
          </Text>
          <TaskAIIcon source={rest.source} creator={rest.creator} isAIRCreatorEnabled={isAIRCreatorEnabled} />
          {status && (
            <>
              <Button
                iconName='more'
                variant='secondary'
                {...getTriggerProps()}
                hoverLabel={t('More options')}
                trackingId={trackingIds.list.openTaskOptions}
              />
              <TaskItemPopoverMenu
                status={status}
                updateTaskStatus={updateTaskStatus}
                deleteTask={() => {
                  close();
                  triggerProps.onClick();
                }}
                getMenuProps={getMenuProps}
                getItemProps={getItemProps}
                onClickEdit={onClickEdit}
                trackingIds={trackingIds}
              />
            </>
          )}
        </div>
        <div className='row'>
          {priority && <TaskPriority priority={priority} />}
          <Chip
            leftElement={<Icon name='calendar-schedule-back' size={16} color='default' />}
            variant='neutral'
            maxWidth='100%'
            css={dueDateChipStyle}
          >
            {date}
          </Chip>
          {!isSingleTypeScope && (
            <Chip leftElement={<Icon name='location' size={16} />} variant='neutral'>
              {location}
            </Chip>
          )}
          {assigneeName && (
            <Chip variant='neutral' leftElement={<Avatar size='xxs' isUser name={assigneeName} />}>
              {assigneeName}
            </Chip>
          )}
        </div>
        <ConfirmationModal {...confirmationModalProps} />
        {isLoading && (
          <div css={loaderStyles}>
            <SpinningLoader size='large' />
          </div>
        )}
        {children}
      </NakedButton>
    );
  }
);

TaskItem.displayName = 'TaskItem';
